import React from 'react';
import { Link } from '@remix-run/react';
import { Sword, Globe, Users, Sparkles } from 'lucide-react';
import { motion } from 'framer-motion';
import { Card } from '~/components/ui/card';
import { H2, Lead } from '~/components/ui/typography';
import { Container, Section } from '~/components/ui/container';
import { ScrollReveal } from '~/components/motion/ScrollReveal';
import { featureGridStagger } from '~/lib/animations';
import { useAnimationState } from '~/lib/motion-utils';

const features = [
  {
    icon: <Sword className="h-8 w-8" />,
    title: 'Dynamic Combat',
    description: 'Real-time combat with strategic depth',
    link: '/database/skills',
  },
  {
    icon: <Globe className="h-8 w-8" />,
    title: 'Vast Open World',
    description: 'Explore a living, breathing fantasy realm',
    link: '/about',
  },
  {
    icon: <Users className="h-8 w-8" />,
    title: 'Deep Customization',
    description: 'Craft your unique hero with endless possibilities',
    link: '/database/classes',
  },
  {
    icon: <Sparkles className="h-8 w-8" />,
    title: 'Epic Storytelling',
    description: 'Shape the narrative with your choices',
    link: '/blog',
  },
];

interface FeatureCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  link: string;
  delay?: number;
}

function FeatureCard({ icon, title, description, link, delay = 0 }: FeatureCardProps) {
  const { getVariants } = useAnimationState();

  return (
    <Link to={link}>
      <motion.div
        className="opacity-0"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
        variants={getVariants(featureGridStagger(delay / 100))}
        whileHover={{ y: -8, transition: { duration: 0.3 } }}
      >
        <Card className="relative overflow-hidden group cursor-pointer border-border/50 bg-card/50 hover:shadow-xl hover:border-primary/30 transition-all duration-300">
          {/* Content */}
          <div className="p-6 pt-12 flex flex-col min-h-[200px] justify-end">
            <motion.div
              className="text-accent mb-3"
              whileHover={{ scale: 1.1, rotate: 5 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            >
              {icon}
            </motion.div>
            <h3 className="text-xl font-bold text-foreground mb-2">{title}</h3>
            <p className="text-muted-foreground text-sm">{description}</p>

            {/* Learn More Link */}
            <div className="mt-4 text-accent text-sm font-medium opacity-100 sm:opacity-0 transform translate-y-0 sm:translate-y-2 transition-all duration-300 group-hover:opacity-100 group-hover:translate-y-0">
              Learn more →
            </div>
          </div>
        </Card>
      </motion.div>
    </Link>
  );
}

interface SectionHeaderProps {
  title: string;
  subtitle: string;
  centered?: boolean;
}

function SectionHeader({ title, subtitle, centered = true }: SectionHeaderProps) {
  return (
    <div className={`flex justify-between items-center ${centered ? 'text-center' : ''}`}>
      <H2 className="text-white">{title}</H2>
      {subtitle && <Lead className="text-gray-400">{subtitle}</Lead>}
    </div>
  );
}

export function FeatureGrid() {
  return (
    <Section className="bg-gradient-to-b from-black to-background">
      <Container>
        <ScrollReveal pattern="fadeDown" immediate className="mb-6 md:mb-10">
          <SectionHeader
            title="Game Features"
            subtitle=""
          />
        </ScrollReveal>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map((feature, index) => (
            <FeatureCard
              key={feature.title}
              {...feature}
              delay={index * 150}
            />
          ))}
        </div>
      </Container>
    </Section>
  );
}

export { SectionHeader };